import * as React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Container } from '../../../components/web/container/container';
import { testimonialsData, Testimonial } from './testimonials.data';

const pillOutline = 'M110 0.5 H192 A27.5 27.5 0 0 1 192 55.5 H28 A27.5 27.5 0 0 1 28 0.5 Z';

const ReviewArrow: React.FC<{ previous?: boolean }> = ({ previous = false }) => (
    <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="block h-[2.4rem] w-[2.4rem] shrink-0 fill-none"
        style={{ transform: previous ? 'rotate(180deg)' : undefined }}
    >
        <path d="M4 12H20M13 5L20 12L13 19" stroke="currentColor" strokeWidth="1.5" />
    </svg>
);

const QuoteAuthor: React.FC<{ testimonial: Testimonial }> = ({ testimonial }) => (
    <figcaption className="mt-[3.2rem] flex items-baseline justify-center gap-[1.2rem] text-[1.4rem]">
        <span className="font-bold text-brand-secondary">{testimonial.name}</span>
        <span className="text-web-copy">{testimonial.date}</span>
    </figcaption>
);

export const Testimonials: React.FC = () => {
    const { t } = useTranslation();
    const reducedMotion = useReducedMotion();
    const [page, setPage] = React.useState(0);
    const sectionRef = React.useRef<HTMLElement>(null);
    const progressRef = React.useRef<SVGPathElement>(null);
    const elapsed = React.useRef(0);
    const [hovered, setHovered] = React.useState(false);
    const [focused, setFocused] = React.useState(false);
    const [paused, setPaused] = React.useState(false);
    const [visible, setVisible] = React.useState(false);
    const stopped = paused || hovered || focused || !visible || reducedMotion;
    const pageCount = testimonialsData.length;
    const lead = testimonialsData[page];

    const changePage = (direction: number) => {
        elapsed.current = 0;
        progressRef.current?.style.setProperty('stroke-dashoffset', '100');
        setPage((current) => (current + direction + pageCount) % pageCount);
    };

    React.useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.4 });
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    React.useEffect(() => {
        if (stopped) return undefined;
        let frame = 0;
        let previous = performance.now();
        const tick = (now: number) => {
            // Hidden tabs must not consume the time available to read a review.
            if (!document.hidden) elapsed.current += Math.min(now - previous, 100);
            previous = now;
            if (elapsed.current >= 8000) {
                elapsed.current = 0;
                setPage((current) => (current + 1) % pageCount);
            }
            progressRef.current?.style.setProperty('stroke-dashoffset', String(100 - (elapsed.current / 8000) * 100));
            frame = window.requestAnimationFrame(tick);
        };
        frame = window.requestAnimationFrame(tick);
        return () => window.cancelAnimationFrame(frame);
    }, [stopped, pageCount]);

    return (
        <section
            ref={sectionRef}
            onFocusCapture={() => setFocused(true)}
            onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
            }}
            aria-labelledby="testimonials-heading"
            className="relative isolate overflow-hidden py-[8rem] md:py-[12rem] xxl:py-[18rem]"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-[30rem] bottom-0 -z-[1] h-full w-[95rem] xxl:-right-[38rem] xxl:w-[130rem] bg-[url('/images/marketing/monery-hero-ribbon-mobile-800.webp')] md:bg-[url('/images/marketing/monery-hero-ribbon-mobile-1280.webp')] vl:bg-[url('/images/marketing/monery-hero-ribbon-v3.png')] bg-cover bg-center opacity-[0.3] [mask-image:radial-gradient(ellipse_at_center,black,transparent_68%)]"
            />
            <Container>
                <h2
                    id="testimonials-heading"
                    className="mx-auto mb-[4.8rem] max-w-[62rem] text-center text-[2.6rem] font-bold leading-tight text-brand-secondary md:mb-[6.4rem] md:text-[3.2rem]"
                >
                    {t('pages.homepage.testimonials.title')}
                </h2>
                <AnimatePresence initial={false} exitBeforeEnter>
                    <motion.div
                        key={page}
                        initial={{ opacity: 0, y: reducedMotion ? 0 : 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: reducedMotion ? 0 : 0.2 }}
                        className="mx-auto flex min-h-[42rem] max-w-[92rem] items-center justify-center sm:min-h-[32rem]"
                        aria-live={stopped ? 'polite' : 'off'}
                        aria-atomic="true"
                    >
                        <figure className="m-0 flex flex-col items-center text-center">
                            <blockquote className="font-[Georgia,serif] text-[2.6rem] leading-[1.45] tracking-[-0.025em] text-web-copy md:text-[3.8rem]">
                                {lead.text}
                            </blockquote>
                            <QuoteAuthor testimonial={lead} />
                        </figure>
                    </motion.div>
                </AnimatePresence>
                <div
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    className="relative mx-auto mt-[3.2rem] flex h-[5.6rem] w-[22rem] items-center justify-between rounded-full p-[0.6rem]"
                >
                    <svg
                        aria-hidden="true"
                        viewBox="0 0 220 56"
                        className="pointer-events-none absolute inset-0 h-[5.6rem] w-[22rem] overflow-visible fill-none"
                    >
                        <path d={pillOutline} strokeWidth="1" stroke="#d9cdc5" />
                        <path
                            ref={progressRef}
                            d={pillOutline}
                            pathLength="100"
                            strokeDasharray="100"
                            strokeDashoffset="100"
                            strokeWidth="1"
                            strokeLinecap="round"
                            className="stroke-brand-primary"
                        />
                    </svg>
                    <button
                        type="button"
                        aria-label={t('pages.homepage.testimonials.previous')}
                        onClick={() => changePage(-1)}
                        className="flex h-[4.4rem] w-[4.4rem] items-center justify-center rounded-full text-[2.2rem] text-brand-secondary transition-colors hover:bg-brand-secondary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-secondary"
                    >
                        <ReviewArrow previous />
                    </button>
                    <button
                        type="button"
                        onClick={() => setPaused((value) => !value)}
                        aria-label={t(`pages.homepage.testimonials.${paused ? 'resume' : 'pause'}`)}
                        aria-pressed={paused}
                        className="flex h-[4.4rem] items-center gap-[0.8rem] rounded-full px-[0.8rem] text-[1.2rem] tabular-nums text-web-copy focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-secondary"
                    >
                        {String(page + 1).padStart(2, '0')} / {String(pageCount).padStart(2, '0')}
                    </button>
                    <button
                        type="button"
                        aria-label={t('pages.homepage.testimonials.next')}
                        onClick={() => changePage(1)}
                        className="flex h-[4.4rem] w-[4.4rem] items-center justify-center rounded-full text-[2.2rem] text-brand-secondary transition-colors hover:bg-brand-secondary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-secondary"
                    >
                        <ReviewArrow />
                    </button>
                </div>
            </Container>
        </section>
    );
};
